import { z } from "zod/v4";

import { z_JsonValue } from "./chat-protocol";
import {
  z_ChatV8ChartWidgetDescriptor,
  z_ChatV8FeedWidgetDescriptor,
  z_ChatV8FeedWidgetMode,
  z_ChatV8InsightKind,
  z_ChatV8InsightWidgetDescriptor,
  z_ChatV8MessagePartRole,
  z_ChatV8PriceChartWidgetDescriptor,
  z_ChatV8StockWidget,
  z_ChatV8PortfolioRatingWidgetData,
  z_ChatV8PresentableTableWidget,
  z_ChatV8TableWidget,
  z_ChatV8UpcomingEventsWidgetData,
  z_ChatV8WidgetType,
} from "./chat-v8-widgets";

export * from "./chat-v8-widgets";
export * from "./chat-v8-elicitations";

import {
  chatV8ElicitationRequestShape,
  z_ChatV8ElicitationAnswer,
  z_ChatV8ElicitationMode,
  z_ChatV8ElicitationRequestPayload,
} from "./chat-v8-elicitations";

export const z_ChatV8RunStatus = z.enum([
  "CREATED",
  "RUNNING",
  "WAITING_FOR_INPUT",
  "WAITING_FOR_APPROVAL",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
]);
export type T_ChatV8RunStatus = z.infer<typeof z_ChatV8RunStatus>;

// Prism run modes (RAI-1040): fast = default pipeline, deep = extended
// research budget. Optional everywhere — modeless sends keep today's
// behavior, and capabilities advertise the options additively so shipped
// clients (non-strict object parse) ignore the new field.
export const CHAT_V8_RUN_MODES = ["fast", "deep"] as const;
export const z_ChatV8RunMode = z.enum(CHAT_V8_RUN_MODES);
export type T_ChatV8RunMode = z.infer<typeof z_ChatV8RunMode>;

export const E_ChatV8PayloadKind = {
  COMPACTION_MARKER: "compaction_marker",
  COMPOSE_RESPONSE_VALIDATION: "compose_response_validation",
  ELICITATION: "elicitation",
  ELICITATION_ANSWERED: "elicitation_answered",
  ELICITATION_WAITING: "elicitation_waiting",
  MESSAGE_PART: "message_part",
  MESSAGE_PART_STREAM: "message_part_stream",
  TOOL_CARD: "tool_card",
  USER_TEXT: "user_text",
  WIDGET_ARTIFACT: "widget_artifact",
} as const;

export const z_ChatV8PayloadKind = z.enum(E_ChatV8PayloadKind);
export type T_ChatV8PayloadKind = z.infer<typeof z_ChatV8PayloadKind>;

export const z_ChatV8EventCategory = z.enum([
  "transcript",
  "renderable",
  "progress",
  "control",
  "context",
]);
export type T_ChatV8EventCategory = z.infer<typeof z_ChatV8EventCategory>;

export const z_ChatV8RenderableKind = z.enum([
  "user_text",
  "message_part",
  "tool_card",
  "approval",
  "elicitation",
  "compaction_marker",
]);
export type T_ChatV8RenderableKind = z.infer<typeof z_ChatV8RenderableKind>;

export const COMPOSE_RESPONSE_TOOL_NAME = "composeResponse" as const;

// Stable capabilities wire values. Shipped mobile clients parse this envelope,
// so removing fields or advertising newer renderable kinds requires a protocol
// version rather than a widget implementation cleanup.
export const CHAT_V8_CAPABILITY_RENDERABLE_KINDS = [
  "user_text",
  "message_part",
  "tool_card",
  "approval",
  "elicitation",
  "compaction_marker",
  "feed_widget",
] as const;
export const z_ChatV8CapabilityRenderableKind = z.enum(
  CHAT_V8_CAPABILITY_RENDERABLE_KINDS,
);

export const z_ChatV8MessagePartWidget = z.discriminatedUnion("widgetType", [
  z.object({
    widgetType: z.literal("stock"),
    widget: z_ChatV8StockWidget,
  }),
  z.object({
    widgetType: z.literal("table"),
    // Presentable: carries the optional deterministic-flow `presentation`
    // upgrade (RAI-1092/RAI-1097). The model-facing schema below stays on the
    // plain table so models can never attach one.
    widget: z_ChatV8PresentableTableWidget,
  }),
  z.object({
    widgetType: z.literal("chart"),
    widget: z_ChatV8ChartWidgetDescriptor,
  }),
  z.object({
    widgetType: z.literal("feed_widget"),
    widget: z_ChatV8FeedWidgetDescriptor,
  }),
  z.object({
    widgetType: z.literal("insight"),
    widget: z_ChatV8InsightWidgetDescriptor,
  }),
]);
export type T_ChatV8MessagePartWidget = z.infer<
  typeof z_ChatV8MessagePartWidget
>;

// Model-facing projection of z_ChatV8MessagePartWidget for the
// generateWidgetArtifact tool: Anthropic's API rejects input_schema with a
// top-level anyOf/oneOf/allOf, so the union nests one level down while the
// wire shape ({ widgetType, widget }) stays identical. The discriminated
// union remains the runtime validator; a guardrail test in
// backend tools/definitions keeps the two aligned.
//
// VIRTUAL AUTHORING TYPES (RAI-1098): "portfolio_rating" and
// "upcoming_events" are model-facing authoring types only. They are NOT wire
// widget types and are deliberately absent from CHAT_V8_WIDGET_TYPES, the
// runtime message-part union, and client capabilities. The artifact factory
// lowers them into the shipped wire shape (widgetType "table" + presentation
// + derived fallback rows), so:
//   - old clients still receive a plain, valid table;
//   - the model authors the RICH schema, never a raw table with a
//     presentation attached, so it still cannot smuggle arbitrary
//     presentation data onto an ordinary table.
export const CHAT_V8_VIRTUAL_WIDGET_AUTHORING_TYPES = [
  "portfolio_rating",
  "upcoming_events",
] as const;

// Current authoring deliberately excludes portfolio_fit (RAI-1100). Its
// production presentation is only a title plus prose, so new answers must put
// that conclusion in the single concise markdown response instead. The full
// descriptor remains in the wire union so historical artifacts still parse.
export const z_ChatV8ModelStockWidget = z_ChatV8StockWidget.strict();
export const z_ChatV8ModelFeedWidgetDescriptor =
  z_ChatV8FeedWidgetDescriptor.strict();

export const z_ChatV8ModelInsightWidgetDescriptor =
  z_ChatV8InsightWidgetDescriptor
    .omit({
      insightKind: true,
      portfolioScenario: true,
      presentationKind: true,
    })
    .extend({
      insightKind: z_ChatV8InsightKind.exclude(["portfolio_fit"]),
    })
    .strict();

export const z_ChatV8GenerateWidgetArtifactModelInput = z.object({
  widgetType: z.enum([
    "stock",
    "table",
    "chart",
    "feed_widget",
    "insight",
    ...CHAT_V8_VIRTUAL_WIDGET_AUTHORING_TYPES,
  ]),
  widget: z.union([
    z_ChatV8ModelStockWidget,
    z_ChatV8TableWidget,
    z_ChatV8PriceChartWidgetDescriptor,
    z_ChatV8ModelFeedWidgetDescriptor,
    z_ChatV8ModelInsightWidgetDescriptor,
    z_ChatV8PortfolioRatingWidgetData,
    z_ChatV8UpcomingEventsWidgetData,
  ]),
});

// Exact execution gate for the model tool. It intentionally diverges from the
// historical wire union in three places: plain tables only, no backend-resolved
// chart presentations, and no prose-only portfolio_fit insight authoring.
export const z_ChatV8GenerateWidgetArtifactRuntimeInput = z.preprocess(
  normalizeChatV8WidgetArtifactInput,
  z.discriminatedUnion("widgetType", [
    z.object({
      widgetType: z.literal("stock"),
      widget: z_ChatV8ModelStockWidget,
    }),
    z.object({ widgetType: z.literal("table"), widget: z_ChatV8TableWidget }),
    z.object({
      widgetType: z.literal("chart"),
      widget: z_ChatV8PriceChartWidgetDescriptor,
    }),
    z.object({
      widgetType: z.literal("feed_widget"),
      widget: z_ChatV8ModelFeedWidgetDescriptor,
    }),
    z.object({
      widgetType: z.literal("insight"),
      widget: z_ChatV8ModelInsightWidgetDescriptor,
    }),
    z.object({
      widgetType: z.literal("portfolio_rating"),
      widget: z_ChatV8PortfolioRatingWidgetData,
    }),
    z.object({
      widgetType: z.literal("upcoming_events"),
      widget: z_ChatV8UpcomingEventsWidgetData,
    }),
  ]),
);

/**
 * Bridge between the two generateWidgetArtifact schemas. The model-facing
 * schema (z_ChatV8GenerateWidgetArtifactModelInput) is flattened — Anthropic
 * rejects top-level anyOf — so models legitimately send insight widgets as
 * {"widgetType":"insight","widget":{"insightKind":"thesis_evidence","symbol":"V"}}.
 * For mismatched cross-pairs admitted by the flattened schema, preserve the
 * descriptor and repair the outer discriminator to its actual shape. Apply via
 * z.preprocess or call directly before z_ChatV8GenerateWidgetArtifactInput.parse.
 */
export function normalizeChatV8WidgetArtifactInput(input: unknown): unknown {
  if (
    typeof input !== "object" ||
    input === null ||
    !("widgetType" in input) ||
    !("widget" in input)
  ) {
    return input;
  }
  const { widget, widgetType } = input;
  if (typeof widget !== "object" || widget === null) {
    return input;
  }

  const portfolioRatingWidget =
    z_ChatV8PortfolioRatingWidgetData.safeParse(widget);
  if (portfolioRatingWidget.success) {
    if (widgetType === "portfolio_rating") return input;
    return {
      ...input,
      widgetType: "portfolio_rating",
      widget: portfolioRatingWidget.data,
    };
  }

  const upcomingEventsWidget =
    z_ChatV8UpcomingEventsWidgetData.safeParse(widget);
  if (upcomingEventsWidget.success) {
    if (widgetType === "upcoming_events") return input;
    return {
      ...input,
      widgetType: "upcoming_events",
      widget: upcomingEventsWidget.data,
    };
  }

  const tableWidget = z_ChatV8TableWidget.safeParse(widget);
  if (tableWidget.success) {
    if (widgetType === "table") return input;
    return { ...input, widgetType: "table", widget: tableWidget.data };
  }

  const chartWidget = z_ChatV8ChartWidgetDescriptor.safeParse(widget);
  if (chartWidget.success) {
    if (widgetType === "chart") return input;
    return { ...input, widgetType: "chart", widget: chartWidget.data };
  }

  const insightWidget = z_ChatV8InsightWidgetDescriptor.safeParse(widget);
  if (insightWidget.success) {
    if (widgetType === "insight") return input;
    return {
      ...input,
      widgetType: "insight",
      widget: insightWidget.data,
    };
  }

  const stockWidget = z_ChatV8StockWidget.safeParse(widget);
  if (stockWidget.success) {
    if (widgetType === "stock") return input;
    return { ...input, widgetType: "stock", widget: stockWidget.data };
  }

  if (
    widgetType === "feed_widget" ||
    ("kind" in widget && widget.kind === "feed_widget")
  ) {
    const feedWidget = z_ChatV8FeedWidgetDescriptor.safeParse(widget);
    if (feedWidget.success) {
      if (widgetType === "feed_widget") return input;
      return { ...input, widgetType: "feed_widget", widget: feedWidget.data };
    }
  }

  return input;
}

export const GENERATE_WIDGET_ARTIFACT_TOOL_NAME =
  "generateWidgetArtifact" as const;

export const z_ChatV8WidgetArtifactPreview = z.record(z.string(), z_JsonValue);
export type T_ChatV8WidgetArtifactPreview = z.infer<
  typeof z_ChatV8WidgetArtifactPreview
>;

export const CHAT_V8_WIDGET_SNAPSHOT_VERSION =
  "chat.v8.widget_snapshot/2" as const;

export const z_ChatV8WidgetSnapshotVersion = z.literal(
  CHAT_V8_WIDGET_SNAPSHOT_VERSION,
);
export type T_ChatV8WidgetSnapshotVersion = z.infer<
  typeof z_ChatV8WidgetSnapshotVersion
>;

export const z_ChatV8WidgetModelContextItem = z.object({
  title: z.string(),
  summary: z.string().optional(),
  symbol: z.string().optional(),
  url: z.string().optional(),
});
export type T_ChatV8WidgetModelContextItem = z.infer<
  typeof z_ChatV8WidgetModelContextItem
>;

export const z_ChatV8WidgetModelContext = z.object({
  title: z.string(),
  summary: z.string(),
  dataQuality: z.string().optional(),
  facts: z.array(z.string()).default([]),
  items: z.array(z_ChatV8WidgetModelContextItem).max(10).optional(),
});
export type T_ChatV8WidgetModelContext = z.infer<
  typeof z_ChatV8WidgetModelContext
>;

export const z_ChatV8WidgetSnapshot = z.object({
  widgetType: z_ChatV8WidgetType,
  data: z_JsonValue,
  status: z.enum(["completed", "partial", "unavailable"]),
  warnings: z.array(z.string()).optional(),
});
export type T_ChatV8WidgetSnapshot = z.infer<typeof z_ChatV8WidgetSnapshot>;

export const z_ChatV8GenerateWidgetArtifactInput = z_ChatV8MessagePartWidget;
export type T_ChatV8GenerateWidgetArtifactInput = z.infer<
  typeof z_ChatV8GenerateWidgetArtifactInput
>;

export const z_ChatV8WidgetArtifactToolOutput = z.object({
  widgetArtifactCuid: z.string().min(1),
  widgetType: z_ChatV8WidgetType,
  modelContext: z_ChatV8WidgetModelContext,
  preview: z_ChatV8WidgetArtifactPreview.optional(),
});
export type T_ChatV8WidgetArtifactToolOutput = z.infer<
  typeof z_ChatV8WidgetArtifactToolOutput
>;

export const z_ChatV8WidgetArtifactPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.WIDGET_ARTIFACT),
    widgetArtifactCuid: z.string().min(1),
    widgetType: z_ChatV8WidgetType,
    inputDescriptor: z_ChatV8MessagePartWidget,
    snapshot: z_ChatV8WidgetSnapshot,
    modelContext: z_ChatV8WidgetModelContext,
    preview: z_ChatV8WidgetArtifactPreview.optional(),
    generatedAt: z.string(),
    snapshotVersion: z_ChatV8WidgetSnapshotVersion,
  })
  .strict();
export type T_ChatV8WidgetArtifactPayload = z.infer<
  typeof z_ChatV8WidgetArtifactPayload
>;

export const z_ChatV8WidgetArtifactStubPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.WIDGET_ARTIFACT),
    widgetArtifactCuid: z.string().min(1),
    widgetType: z_ChatV8WidgetType,
    modelContext: z_ChatV8WidgetModelContext,
    preview: z_ChatV8WidgetArtifactPreview.optional(),
    generatedAt: z.string(),
    snapshotOmitted: z.literal(true),
    snapshotVersion: z_ChatV8WidgetSnapshotVersion,
  })
  .strict();
export type T_ChatV8WidgetArtifactStubPayload = z.infer<
  typeof z_ChatV8WidgetArtifactStubPayload
>;

export const z_ChatV8WidgetArtifactSnapshotResponse = z.object({
  widgetArtifactCuid: z.string().min(1),
  widgetType: z_ChatV8WidgetType,
  modelContext: z_ChatV8WidgetModelContext,
  preview: z_ChatV8WidgetArtifactPreview.optional(),
  generatedAt: z.string(),
  snapshot: z_ChatV8WidgetSnapshot,
  snapshotVersion: z_ChatV8WidgetSnapshotVersion,
});
export type T_ChatV8WidgetArtifactSnapshotResponse = z.infer<
  typeof z_ChatV8WidgetArtifactSnapshotResponse
>;

export function containsInlineChartFence(markdown: string): boolean {
  return /```chart(?:\s|$)/iu.test(markdown);
}

export const z_ChatV8GeneratedMarkdown = z
  .string()
  .refine(
    (markdown) => !containsInlineChartFence(markdown),
    "Inline chart blocks are unsupported; use native V8 widget artifacts",
  );

export const z_ChatV8ComposeResponseTextBlock = z.object({
  text: z.object({
    markdown: z_ChatV8GeneratedMarkdown,
  }),
});

export const z_ChatV8ComposeResponseWidgetRefBlock = z.object({
  widgetRef: z.object({
    widgetArtifactCuid: z.string().min(1),
  }),
});

export const z_ChatV8ComposeResponseBlock = z.union([
  z_ChatV8ComposeResponseTextBlock,
  z_ChatV8ComposeResponseWidgetRefBlock,
]);
export type T_ChatV8ComposeResponseBlock = z.infer<
  typeof z_ChatV8ComposeResponseBlock
>;

export const z_ChatV8ComposeResponseInput = z.object({
  blocks: z.array(z_ChatV8ComposeResponseBlock).min(1),
});
export type T_ChatV8ComposeResponseInput = z.infer<
  typeof z_ChatV8ComposeResponseInput
>;

export const z_ChatV8MessagePartMarkdownPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.MESSAGE_PART),
    partKind: z.literal("markdown"),
    messagePartRole: z_ChatV8MessagePartRole.optional(),
    markdown: z.string(),
    liveSequence: z.number().int().min(0).optional(),
    partIndex: z.number().int().min(0).optional(),
    live: z.literal(true).optional(),
    snapshot: z.literal(true).optional(),
    snapshotUpdatedAt: z.string().optional(),
  })
  .strict();
export type T_ChatV8MessagePartMarkdownPayload = z.infer<
  typeof z_ChatV8MessagePartMarkdownPayload
>;

export const z_ChatV8MessagePartMarkdownDeltaPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.MESSAGE_PART),
    partKind: z.literal("markdown"),
    messagePartRole: z_ChatV8MessagePartRole.optional(),
    delta: z.string(),
    markdown: z.string(),
    offset: z.number().int().min(0),
    liveSequence: z.number().int().min(0),
    live: z.literal(true).optional(),
  })
  .strict();
export type T_ChatV8MessagePartMarkdownDeltaPayload = z.infer<
  typeof z_ChatV8MessagePartMarkdownDeltaPayload
>;

export const z_ChatV8MessagePartWidgetPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.MESSAGE_PART),
    partKind: z.literal("widget"),
    messagePartRole: z_ChatV8MessagePartRole.optional(),
    widgetType: z_ChatV8WidgetType,
    state: z.enum(["started", "streaming", "completed", "failed"]),
    partIndex: z.number().int().min(0).optional(),
    preview: z.record(z.string(), z_JsonValue).optional(),
    widgetArtifactCuid: z.string().optional(),
    snapshot: z_ChatV8WidgetSnapshot.optional(),
  })
  .strict();
export type T_ChatV8MessagePartWidgetPayload = z.infer<
  typeof z_ChatV8MessagePartWidgetPayload
>;

export const z_ChatV8CompactionMarkerPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.COMPACTION_MARKER),
    summary: z.string(),
    sourceStartSeq: z.number().int().min(1),
    sourceEndSeq: z.number().int().min(1),
    sourceDisplayItemCount: z.number().int().min(0),
    preservedRecentDisplayItemCount: z.number().int().min(0),
    estimatedTokenCountBefore: z.number().int().min(0),
    estimatedTokenCountAfter: z.number().int().min(0),
  })
  .strict();
export type T_ChatV8CompactionMarkerPayload = z.infer<
  typeof z_ChatV8CompactionMarkerPayload
>;

/**
 * Reference to one uploaded attachment on a user message. Deliberately does
 * not carry object paths or signed URLs: clients resolve previews and
 * downloads through the authorized read endpoints, keyed by the owning item.
 */
export const z_ChatV8AttachmentRef = z
  .object({
    attachmentCuid: z.string().min(1).max(40),
    type: z.enum(["IMAGE", "PDF", "CSV", "SPREADSHEET"]),
    fileName: z.string().min(1).max(255).optional(),
    byteSize: z.number().int().positive().optional(),
    previewWidth: z.number().int().positive().optional(),
    previewHeight: z.number().int().positive().optional(),
  })
  .strict();
export type T_ChatV8AttachmentRef = z.infer<typeof z_ChatV8AttachmentRef>;

export const CHAT_V8_MAX_ATTACHMENTS_PER_MESSAGE = 4;

export const z_ChatV8UserTextPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.USER_TEXT),
    text: z.string(),
    edited: z.literal(true).optional(),
    editedAt: z.string().optional(),
    attachments: z
      .array(z_ChatV8AttachmentRef)
      .max(CHAT_V8_MAX_ATTACHMENTS_PER_MESSAGE)
      .optional(),
  })
  .strict();
export type T_ChatV8UserTextPayload = z.infer<typeof z_ChatV8UserTextPayload>;

export const z_ChatV8MessagePartPayload = z.union([
  z_ChatV8MessagePartMarkdownDeltaPayload,
  z_ChatV8MessagePartMarkdownPayload,
  z_ChatV8MessagePartWidgetPayload,
]);
export type T_ChatV8MessagePartPayload = z.infer<
  typeof z_ChatV8MessagePartPayload
>;

export const z_ChatV8MessagePartStreamPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.MESSAGE_PART_STREAM),
    itemCuid: z.string(),
    partCuid: z.string(),
    markdown: z.string(),
    messagePartRole: z_ChatV8MessagePartRole,
    liveSequence: z.number().int().min(0),
    updatedAt: z.string(),
  })
  .strict();
export type T_ChatV8MessagePartStreamPayload = z.infer<
  typeof z_ChatV8MessagePartStreamPayload
>;

export const z_ChatV8ToolCardPhase = z.enum([
  "selected",
  "input_ready",
  "completed",
  "failed",
]);
export type T_ChatV8ToolCardPhase = z.infer<typeof z_ChatV8ToolCardPhase>;

export const z_ChatV8ToolCardPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.TOOL_CARD),
    phase: z_ChatV8ToolCardPhase,
    selectedAt: z.string(),
    startedAt: z.string(),
    state: z.enum(["running", "completed", "failed"]),
    title: z.string(),
    inputReadyAt: z.string().optional(),
    completedAt: z.string().optional(),
    durationMs: z.number().int().min(0).optional(),
    failedPhase: z_ChatV8ToolCardPhase.optional(),
  })
  .strict();
export type T_ChatV8ToolCardPayload = z.infer<typeof z_ChatV8ToolCardPayload>;

export const z_ChatV8WidgetArtifactEventPayload = z.union([
  z_ChatV8WidgetArtifactPayload,
  z_ChatV8WidgetArtifactStubPayload,
]);
export type T_ChatV8WidgetArtifactEventPayload = z.infer<
  typeof z_ChatV8WidgetArtifactEventPayload
>;

export const z_ChatV8ElicitationPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.ELICITATION),
    ...chatV8ElicitationRequestShape,
  })
  .strict();
export type T_ChatV8ElicitationPayload = z.infer<
  typeof z_ChatV8ElicitationPayload
>;

const z_ChatV8ToolElicitationWaitingPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.ELICITATION_WAITING),
    partCuid: z.string(),
    itemCuid: z.string(),
    request: z_ChatV8ElicitationRequestPayload,
    requestedToolName: z.string(),
    toolCallCuid: z.string(),
  })
  .strict();

const z_ChatV8ScriptedElicitationWaitingPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.ELICITATION_WAITING),
    flowName: z.string(),
    itemCuid: z.string(),
    request: z_ChatV8ElicitationRequestPayload,
    requestedToolName: z.null(),
  })
  .strict();

export const z_ChatV8ElicitationWaitingPayload = z.union([
  z_ChatV8ToolElicitationWaitingPayload,
  z_ChatV8ScriptedElicitationWaitingPayload,
]);
export type T_ChatV8ElicitationWaitingPayload = z.infer<
  typeof z_ChatV8ElicitationWaitingPayload
>;

export const z_ChatV8ElicitationAnsweredResumePayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.ELICITATION_ANSWERED),
    elicitationEventCuid: z.string(),
    answerEventCuid: z.string(),
    answeredItemCuid: z.string(),
  })
  .strict();
export type T_ChatV8ElicitationAnsweredResumePayload = z.infer<
  typeof z_ChatV8ElicitationAnsweredResumePayload
>;

export const z_ChatV8ComposeResponseValidationPayload = z
  .object({
    kind: z.literal(E_ChatV8PayloadKind.COMPOSE_RESPONSE_VALIDATION),
    reason: z.literal("unknown_widget_artifact"),
    partIndex: z.number().int().min(0),
    widgetArtifactCuid: z.string(),
  })
  .strict();
export type T_ChatV8ComposeResponseValidationPayload = z.infer<
  typeof z_ChatV8ComposeResponseValidationPayload
>;

export const z_ChatV8RunCreatedPayload = z
  .object({
    routeName: z.string(),
    routeReason: z.string(),
  })
  .strict();
export type T_ChatV8RunCreatedPayload = z.infer<
  typeof z_ChatV8RunCreatedPayload
>;

export const CHAT_V8_DETERMINISTIC_ROUTE_NAMES = [
  "portfolio_rating",
  "portfolio_recap",
  "portfolio_events",
  "company_analysis",
  "holdings_view",
  "news_feed",
  "alerts_view",
] as const;
export const z_ChatV8DeterministicRouteName = z.enum(
  CHAT_V8_DETERMINISTIC_ROUTE_NAMES,
);

export const z_ChatV8RunStartedPayload = z
  .object({
    routeName: z.string(),
  })
  .strict();
export type T_ChatV8RunStartedPayload = z.infer<
  typeof z_ChatV8RunStartedPayload
>;

export const z_ChatV8EmptyRunControlPayload = z.record(z.string(), z.never());
export type T_ChatV8EmptyRunControlPayload = z.infer<
  typeof z_ChatV8EmptyRunControlPayload
>;

export const z_ChatV8RunFailedPayload = z
  .object({
    message: z.string(),
  })
  .strict();
export type T_ChatV8RunFailedPayload = z.infer<typeof z_ChatV8RunFailedPayload>;

export const z_ChatV8RunSupersededPayload = z
  .object({
    reason: z.literal("superseded_by_new_user_message"),
  })
  .strict();
export type T_ChatV8RunSupersededPayload = z.infer<
  typeof z_ChatV8RunSupersededPayload
>;

export type T_ChatV8RunControlEventPayload =
  | {
      payload: T_ChatV8RunCreatedPayload;
      type: "run.created";
    }
  | {
      payload: T_ChatV8RunStartedPayload;
      type: "run.started";
    }
  | {
      payload: T_ChatV8EmptyRunControlPayload;
      type: "run.completed";
    }
  | {
      payload: T_ChatV8RunFailedPayload;
      type: "run.failed";
    }
  | {
      payload: T_ChatV8EmptyRunControlPayload | T_ChatV8RunSupersededPayload;
      type: "run.cancelled";
    };

export const z_ChatV8RunControlPayload = z.union([
  z_ChatV8RunCreatedPayload,
  z_ChatV8RunStartedPayload,
  z_ChatV8EmptyRunControlPayload,
  z_ChatV8RunFailedPayload,
  z_ChatV8RunSupersededPayload,
]);
export type T_ChatV8RunControlPayload = z.infer<
  typeof z_ChatV8RunControlPayload
>;

export const z_ChatV8PhasePayload = z
  .object({
    phase: z.literal("tool_exposure"),
    availableBundleNames: z.array(z.string()),
    deterministicRouteUsed: z.literal(false),
    initialToolNames: z.array(z.string()),
    nativeWebSearchAllowed: z.literal(true),
    routeName: z.literal("model_controlled"),
    routeReason: z.literal("model_decides_tool_bundles"),
    toolExposurePolicyName: z.string(),
    visibleToolNames: z.array(z.string()),
  })
  .strict();
export type T_ChatV8PhasePayload = z.infer<typeof z_ChatV8PhasePayload>;

export const z_ChatV8TimelineRewrittenPayload = z
  .object({
    fromSeq: z.number().int().min(1),
    reason: z.string(),
    timelineVersion: z.number().int().min(1),
  })
  .strict();
export type T_ChatV8TimelineRewrittenPayload = z.infer<
  typeof z_ChatV8TimelineRewrittenPayload
>;

export const z_ChatV8ElicitationAnswerEventPayload = z
  .object({
    answer: z_ChatV8ElicitationAnswer,
    requestedEventCuid: z.string(),
    requestedItemCuid: z.string(),
  })
  .strict();
export type T_ChatV8ElicitationAnswerEventPayload = z.infer<
  typeof z_ChatV8ElicitationAnswerEventPayload
>;

export const z_ChatV8ApprovalResolvedPayload = z
  .object({
    actionCuid: z.string(),
    approved: z.boolean(),
    reason: z.string().nullable(),
  })
  .strict();
export type T_ChatV8ApprovalResolvedPayload = z.infer<
  typeof z_ChatV8ApprovalResolvedPayload
>;

export const z_ChatV8KnownKindPayload = z.union([
  z_ChatV8UserTextPayload,
  z_ChatV8MessagePartPayload,
  z_ChatV8MessagePartStreamPayload,
  z_ChatV8ToolCardPayload,
  z_ChatV8WidgetArtifactEventPayload,
  z_ChatV8ElicitationPayload,
  z_ChatV8ElicitationWaitingPayload,
  z_ChatV8ElicitationAnsweredResumePayload,
  z_ChatV8CompactionMarkerPayload,
  z_ChatV8ComposeResponseValidationPayload,
]);
export type T_ChatV8KnownKindPayload = z.infer<typeof z_ChatV8KnownKindPayload>;

export const z_ChatV8RecognizedEventPayload = z.union([
  z_ChatV8KnownKindPayload,
  z_ChatV8RunControlPayload,
  z_ChatV8PhasePayload,
  z_ChatV8TimelineRewrittenPayload,
  z_ChatV8ElicitationAnswerEventPayload,
  z_ChatV8ApprovalResolvedPayload,
]);
export type T_ChatV8RecognizedEventPayload = z.infer<
  typeof z_ChatV8RecognizedEventPayload
>;

// The brand is type-only: parsing returns the original raw JSON record
// unchanged. It prevents callers from constructing an unchecked fallback
// directly while unknown, malformed, or future rows still load safely.
export const z_ChatV8UnknownEventPayload = z
  .record(z.string(), z_JsonValue)
  .brand<"ChatV8UnknownEventPayload">();
export type T_ChatV8UnknownEventPayload = z.infer<
  typeof z_ChatV8UnknownEventPayload
>;

export const z_ChatV8EventPayload = z.union([
  z_ChatV8RecognizedEventPayload,
  z_ChatV8UnknownEventPayload,
]);
export type T_ChatV8EventPayload = z.infer<typeof z_ChatV8EventPayload>;

export function isChatV8PayloadKind<
  T_Kind extends T_ChatV8KnownKindPayload["kind"],
>(
  payload: T_ChatV8EventPayload,
  kind: T_Kind,
): payload is Extract<T_ChatV8KnownKindPayload, { kind: T_Kind }> {
  const parsed = z_ChatV8KnownKindPayload.safeParse(payload);
  return parsed.success && parsed.data.kind === kind;
}

export function isChatV8UnknownEventPayload(
  payload: T_ChatV8EventPayload,
): payload is T_ChatV8UnknownEventPayload {
  return !z_ChatV8RecognizedEventPayload.safeParse(payload).success;
}

export const z_ChatV8ProviderMetadata = z
  .object({
    chartIndex: z.number().int().min(0).optional(),
    dynamic: z.boolean().optional(),
    errorPreview: z.string().optional(),
    executionDurationMs: z.number().min(0).optional(),
    forceCompactionDebug: z.boolean().optional(),
    hardTokenThreshold: z.number().int().min(0).optional(),
    inputGenerationDurationMs: z.number().min(0).optional(),
    inputPreview: z.string().optional(),
    legacyCompactionGeneratedAt: z.string().nullable().optional(),
    legacyCompactionMessageCount: z.number().int().nullable().optional(),
    legacyImport: z.boolean().optional(),
    legacySource: z.literal("chat_thread_v2").optional(),
    memory: z_JsonValue.optional(),
    modelContext: z.string().optional(),
    outputPreview: z.string().optional(),
    phase: z_ChatV8ToolCardPhase.optional(),
    preliminary: z.boolean().optional(),
    priorCompactionEventCuid: z.string().nullable().optional(),
    providerExecuted: z.boolean().optional(),
    providerMetadataPreview: z.string().optional(),
    renderInput: z_ChatV8ElicitationRequestPayload.optional(),
    sdkToolCallId: z.string().optional(),
    serializedTranscriptPreview: z.string().optional(),
    source: z.enum(["fenced_block", "tool_call"]).optional(),
    sourceEventCount: z.number().int().min(0).optional(),
    toolCallCuid: z.string().optional(),
    toolDisplayName: z.string().optional(),
    toolMetadataPreview: z.string().optional(),
    toolName: z.string().optional(),
    totalDurationMs: z.number().min(0).optional(),
    warningTokenThreshold: z.number().int().min(0).optional(),
  })
  .catchall(z_JsonValue);
export type T_ChatV8ProviderMetadata = z.infer<typeof z_ChatV8ProviderMetadata>;

export const z_ChatV8KnownRunMetadata = z
  .object({
    mode: z_ChatV8RunMode,
    routeReason: z.string(),
  })
  .strict();
export type T_ChatV8KnownRunMetadata = z.infer<typeof z_ChatV8KnownRunMetadata>;

export const z_ChatV8UnknownRunMetadata = z
  .record(z.string(), z_JsonValue)
  .brand<"ChatV8UnknownRunMetadata">();

export const z_ChatV8RunMetadata = z.union([
  z_ChatV8KnownRunMetadata,
  z_ChatV8UnknownRunMetadata,
]);
export type T_ChatV8RunMetadata = z.infer<typeof z_ChatV8RunMetadata>;

export const z_ChatV8ApprovalResumePayload = z
  .object({
    approvalEventCuid: z.string(),
    approvalResolutionEventCuid: z.string(),
  })
  .strict();
export type T_ChatV8ApprovalResumePayload = z.infer<
  typeof z_ChatV8ApprovalResumePayload
>;

export const z_ChatV8UnknownRunResumeState = z
  .record(z.string(), z_JsonValue)
  .brand<"ChatV8UnknownRunResumeState">();

export const z_ChatV8RunResumeState = z.union([
  z_ChatV8MessagePartStreamPayload,
  z_ChatV8ElicitationWaitingPayload,
  z_ChatV8ElicitationAnsweredResumePayload,
  z_ChatV8ApprovalResumePayload,
  z_ChatV8UnknownRunResumeState,
]);
export type T_ChatV8RunResumeState = z.infer<typeof z_ChatV8RunResumeState>;

export const z_ChatV8Event = z.object({
  schemaVersion: z.literal("chat.v8.event/1"),
  eventCuid: z.string(),
  threadCuid: z.string(),
  runCuid: z.string().nullable(),
  turnCuid: z.string(),
  seq: z.number(),
  timelineVersion: z.number(),
  displaySeq: z.number().nullable(),
  type: z.string(),
  category: z_ChatV8EventCategory,
  status: z.string().nullable(),
  itemCuid: z.string().nullable(),
  partCuid: z.string().nullable(),
  toolCallCuid: z.string().nullable(),
  actionCuid: z.string().nullable(),
  payload: z_ChatV8EventPayload,
  providerMetadata: z_ChatV8ProviderMetadata.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type T_ChatV8Event = z.infer<typeof z_ChatV8Event>;

export const z_ChatV8Run = z.object({
  cuid: z.string(),
  threadCuid: z.string(),
  turnCuid: z.string(),
  status: z_ChatV8RunStatus,
  timelineVersion: z.number(),
  triggerEventCuid: z.string().nullable(),
  route: z.string().nullable(),
  resumeState: z_ChatV8RunResumeState.nullable(),
  error: z.string().nullable(),
  metadata: z_ChatV8RunMetadata.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  cancellationRequestedAt: z.string().nullable(),
});
export type T_ChatV8Run = z.infer<typeof z_ChatV8Run>;

export const z_ChatV8ThreadPage = z.object({
  threadCuid: z.string(),
  threadName: z.string().nullable(),
  timelineVersion: z.number(),
  latestSeq: z.number(),
  latestDisplaySeq: z.number(),
  events: z.array(z_ChatV8Event),
  hasMoreBefore: z.boolean(),
  oldestDisplaySeq: z.number().nullable(),
  newestDisplaySeq: z.number().nullable(),
  activeRun: z_ChatV8Run.nullable(),
});
export type T_ChatV8ThreadPage = z.infer<typeof z_ChatV8ThreadPage>;

export const z_ChatV8ThreadListItem = z.object({
  threadCuid: z.string(),
  threadName: z.string().nullable(),
  timelineVersion: z.number(),
  latestSeq: z.number(),
  latestDisplaySeq: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type T_ChatV8ThreadListItem = z.infer<typeof z_ChatV8ThreadListItem>;

export const z_ChatV8ThreadList = z.object({
  nextCursor: z.string().nullable(),
  threads: z.array(z_ChatV8ThreadListItem),
});
export type T_ChatV8ThreadList = z.infer<typeof z_ChatV8ThreadList>;

export const z_ChatV8RunModeOption = z.object({
  mode: z_ChatV8RunMode,
  label: z.string().min(1),
  description: z.string().min(1),
});
export type T_ChatV8RunModeOption = z.infer<typeof z_ChatV8RunModeOption>;

export const z_ChatV8Capabilities = z.object({
  schemaVersion: z.literal("chat.v8.capabilities/1"),
  transports: z.array(z.enum(["query", "subscription"])),
  eventCategories: z.array(z_ChatV8EventCategory),
  renderableKinds: z.array(z_ChatV8CapabilityRenderableKind),
  elicitationModes: z.array(z_ChatV8ElicitationMode),
  supportsTimelineRewrite: z.literal(true),
  supportsInThreadCompaction: z.literal(true),
  supportsInlineCharts: z.boolean(),
  supportsPremadeWidgets: z.literal(true),
  supportsWidgetArtifacts: z.literal(true),
  supportsWidgetArtifactRefs: z.literal(true),
  supportsImmutableWidgetSnapshots: z.literal(true),
  supportsLazyWidgetSnapshotLoading: z.literal(true),
  composeResponseWidgetMode: z.literal("artifact_ref"),
  supportsFreeformCharts: z.literal(false),
  supportsFeedWidget: z.literal(true),
  feedWidgetModes: z.array(z_ChatV8FeedWidgetMode),
  supportedWidgetTypes: z.array(z_ChatV8WidgetType),
  supportsLiveTextDeltas: z.literal(true),
  // Additive (RAI-1040): clients render the model-picker pill from this
  // server truth; absence means the picker should not render.
  availableRunModes: z.array(z_ChatV8RunModeOption).optional(),
  // Additive (RAI-1034): clients show attachment UI only when the backend
  // accepts attachment refs on sendMessage; absence means text-only.
  supportsUserImageAttachments: z.boolean().optional(),
  // Additive: which attachment kinds sendMessage accepts. Clients derive
  // pickers and accept lists from this; absence means images only.
  supportedAttachmentKinds: z
    .array(z.enum(["image", "pdf", "csv", "spreadsheet"]))
    .optional(),
  supportsStreamingSnapshots: z.literal(true),
  supportsMessageParts: z.literal(true),
  supportsMessagePartRoles: z.literal(true),
  messagePartRoles: z.array(z_ChatV8MessagePartRole),
  supportsTerminalComposeResponse: z.literal(true),
  supportsWidgetLifecycleEvents: z.literal(true),
  supportsToolProgressEvents: z.literal(true),
  supportsEarlyToolStartEvents: z.literal(true),
  supportsToolCallPhaseEvents: z.literal(true),
  supportsToolCallElicitation: z.literal(true),
  toolCallDurationSemantics: z.literal("execution_only"),
  durableWidgetLifecycleEvents: z.literal(true),
  durableToolLifecycleEvents: z.literal(true),
  durableToolInputDeltas: z.literal(false),
  durableMarkdownDeltas: z.literal(false),
  durableTokenDeltas: z.literal(false),
});
export type T_ChatV8Capabilities = z.infer<typeof z_ChatV8Capabilities>;
